import { useState } from "react";

import { PencilIcon, PlusIcon } from "@phosphor-icons/react";
import { startCase } from "lodash";

import { Button, classNames, Text } from "@alpacahq/ui/old";

import type { EquityWithSymbol } from "~/v2/pages/dashboard/funding/common/types";
import { EquityInput } from "./EquityInput";

export interface StocksBoxProps {
  equities: EquityWithSymbol[];
  onSave: (equity: EquityWithSymbol) => void;
  onDelete: (equity: EquityWithSymbol) => void;
}

type State = {
  isAddingEquity: boolean;
  editingEquity: EquityWithSymbol | null;
};

export const StocksBox = ({ equities, onSave, onDelete }: StocksBoxProps) => {
  const [{ isAddingEquity, editingEquity }, setState] = useState<State>({
    isAddingEquity: false,
    editingEquity: null,
  });

  const hasContent = isAddingEquity || equities.length > 0;

  const onAddEquityClicked = () => {
    setState({
      isAddingEquity: true,
      editingEquity: null,
    });
  };

  const onEditEquityClicked = (equity: EquityWithSymbol) => () => {
    setState({
      isAddingEquity: true,
      editingEquity: equity,
    });
  };

  const onSaveEquity = (equity: EquityWithSymbol) => {
    onSave(equity);
    setState({
      isAddingEquity: false,
      editingEquity: null,
    });
  };

  const onDeleteEquity = (equity?: EquityWithSymbol) => {
    equity && onDelete(equity);
    setState({
      isAddingEquity: false,
      editingEquity: null,
    });
  };

  return (
    <div
      className={classNames(
        "flex min-h-[67px] flex-col justify-center rounded border border-gray-200 p-4 transition-all",
        isAddingEquity && "border-gray-500",
      )}
    >
      <div className="flex min-h-8 items-center justify-between gap-4">
        <h2 className="m-0 p-0 font-medium text-base text-gray-800">
          Stocks & ETFs
        </h2>
        <Button
          variant="v2/ghost"
          size="v2/small"
          onClick={onAddEquityClicked}
          type="button"
        >
          <PlusIcon size={16} />
          <span>Add Asset</span>
        </Button>
      </div>

      <div
        className={classNames(
          "border-t border-t-cool-gray-300 opacity-0 transition-all",
          hasContent && "my-4 opacity-100",
        )}
      />

      <div
        className={classNames(
          "pointer-events-none flex max-h-0 flex-col opacity-0 transition-all",
          equities.length > 0 &&
            "pointer-events-auto max-h-[2000px] gap-4 opacity-100",
          equities.length > 0 && isAddingEquity && "pb-4",
        )}
      >
        {equities.map((equity) => (
          <div key={equity.symbol} className="flex justify-between">
            <Text>{equity.symbol}</Text>
            <div className="flex items-center justify-end gap-1">
              <Text>
                {equity.qty ? equity.qty : "Full Amount"}{" "}
                {`(${startCase(equity.position.toLowerCase())})`}
              </Text>
              <button type="button" onClick={onEditEquityClicked(equity)}>
                <PencilIcon size={20} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <EquityInput
        editEquity={editingEquity}
        onSave={onSaveEquity}
        onDelete={onDeleteEquity}
        isDisplayed={isAddingEquity}
      />
    </div>
  );
};
